import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { NotFoundPage } from './NotFoundPage';
import { Clock, ArrowLeft, Calendar } from 'lucide-react';
import { getPost, posts, formatDate } from '@/app/content/blog';
import { mdxComponents } from '@/app/components/blog/BlogMdxComponents';
import { blogPostPageContent } from '@/app/content/uiContent';
import { BlogCard } from '@/app/components/blog/BlogCard';
import { NAVIGATION } from '@/config/constants';

function ReadingProgress() {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const update = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(docHeight > 0 ? (scrollTop / docHeight) * 100 : 0);
    };
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, []);

  return (
    <div
      role="progressbar"
      aria-label="Postęp czytania"
      aria-valuenow={Math.round(progress)}
      aria-valuemin={0}
      aria-valuemax={100}
      className="fixed top-0 left-0 right-0 h-[3px] z-[60] bg-gray-100"
    >
      <div
        className="h-full bg-[#004f4f] transition-[width] duration-75 ease-out transition-all"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}

const CATEGORY_STYLES: Record<string, string> = {
  Prawo: 'bg-teal-50 text-[#004f4f] ring-1 ring-teal-200',
  Dane: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200',
  Metodologia: 'bg-slate-100 text-slate-700 ring-1 ring-slate-200',
};

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const mod = slug ? getPost(slug) : undefined;
  const meta = mod?.frontmatter;

  useEffect(() => {
    if (!meta) return;

    const ogTitle = meta.og_title ?? meta.title;
    const metaDesc = meta.meta_description ?? meta.description;

    const prevTitle = document.title;
    document.title = `${ogTitle} — BastunIT`;

    const descEl = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const ogTitleEl = document.querySelector<HTMLMetaElement>('meta[property="og:title"]');
    const ogDescEl = document.querySelector<HTMLMetaElement>('meta[property="og:description"]');
    const twTitleEl = document.querySelector<HTMLMetaElement>('meta[name="twitter:title"]');
    const twDescEl = document.querySelector<HTMLMetaElement>('meta[name="twitter:description"]');

    const prevDesc = descEl?.content ?? '';
    const prevOgTitle = ogTitleEl?.content ?? '';
    const prevOgDesc = ogDescEl?.content ?? '';
    const prevTwTitle = twTitleEl?.content ?? '';
    const prevTwDesc = twDescEl?.content ?? '';

    if (descEl) descEl.content = metaDesc;
    if (ogTitleEl) ogTitleEl.content = ogTitle;
    if (ogDescEl) ogDescEl.content = metaDesc;
    if (twTitleEl) twTitleEl.content = ogTitle;
    if (twDescEl) twDescEl.content = metaDesc;

    return () => {
      document.title = prevTitle;
      if (descEl) descEl.content = prevDesc;
      if (ogTitleEl) ogTitleEl.content = prevOgTitle;
      if (ogDescEl) ogDescEl.content = prevOgDesc;
      if (twTitleEl) twTitleEl.content = prevTwTitle;
      if (twDescEl) twDescEl.content = prevTwDesc;
    };
  }, [meta]);

  if (!mod || !meta) {
    return <NotFoundPage />;
  }

  const PostContent = mod.default;
  const categoryStyle = CATEGORY_STYLES[meta.category] ?? 'bg-gray-100 text-gray-700 ring-1 ring-gray-200';
  const related = posts.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <>
      <ReadingProgress />
      <main id="main-content" tabIndex={-1}>
        {/* Breadcrumb */}
        <div className="bg-white border-b border-gray-100 px-6 py-4">
          <div className="max-w-7xl mx-auto">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#004f4f] focus:outline-none focus:ring-2 focus:ring-[#004f4f] rounded transition-colors"
            >
              <ArrowLeft size={15} aria-hidden="true" />
              {blogPostPageContent.backToListLabel}
            </Link>
          </div>
        </div>

        {/* Post header */}
        <header className="bg-white px-6 pt-12 pb-10">
          <div className="max-w-2xl mx-auto">
            <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full mb-6 ${categoryStyle}`}>
              {meta.category}
            </span>
            <h1 className="text-[clamp(1.75rem,4vw,2.5rem)] font-bold text-slate-900 tracking-tight leading-tight mb-5">
              {meta.title}
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8">
              {meta.description}
            </p>
            <div className="flex flex-wrap items-center gap-5 text-sm text-slate-600 pb-10 border-b border-gray-200">
              <span className="flex items-center gap-2">
                <Calendar size={14} aria-hidden="true" />
                <time dateTime={meta.date}>{formatDate(meta.date)}</time>
              </span>
              <span className="flex items-center gap-2">
                <Clock size={14} aria-hidden="true" />
                {meta.readingTime} {blogPostPageContent.readingTimeSuffix}
              </span>
            </div>
          </div>
        </header>

        {/* Article body */}
        <article className="px-6 pb-16 bg-white" aria-label={meta.title}>
          <div className="max-w-2xl mx-auto">
            <PostContent components={mdxComponents} />
          </div>
        </article>

        {/* Bottom CTA */}
        <section className="bg-gray-50 border-t border-gray-200 px-6 py-14" aria-label="Sprawdź dostępność swojej strony">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-xs font-semibold text-[#004f4f] uppercase tracking-widest mb-4">
              {blogPostPageContent.nextStepKicker}
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight mb-4">
              {blogPostPageContent.nextStepHeading}
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-8 max-w-lg mx-auto">
              {blogPostPageContent.nextStepDescription}
            </p>
            <Link
              to={`/#${NAVIGATION.contact.id}`}
              className="inline-flex items-center gap-3 bg-[#004f4f] text-white px-8 py-4 rounded-full font-bold hover:bg-[#003d3d] focus:outline-none focus:ring-4 focus:ring-[#004f4f] focus:ring-offset-4 transition-colors"
            >
              {blogPostPageContent.nextStepCtaLabel}
            </Link>
          </div>
        </section>

        {/* Related posts */}
        {related.length > 0 && (
          <section className="bg-white border-t border-gray-100 px-6 py-14" aria-label="Powiązane artykuły">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl font-bold text-slate-900 mb-8">
                {blogPostPageContent.relatedHeading}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {related.map((post) => (
                  <BlogCard key={post.slug} post={post} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}
